/**
 * Media-tellingen per wasserij-item — voedt werklijst-badges zoals
 * "nog geen tijdens-foto's" op de Foto Tijdens-lijst.
 *
 * Twee bronnen die samen de waarheid vormen:
 * 1. de media-tabel (server, via GET /api/media?wiSfIds=…) — alles wat al
 *    geüpload en geregistreerd is;
 * 2. de lokale Dexie mediaQueue — captures die nog op upload wachten. Zonder
 *    deze bron blijft een kleed op "nog te doen" staan terwijl de wasser er
 *    net naast stond met de camera.
 */
import { db } from '../db/database';
import { useAuthStore } from '../../stores';
import type { MediaFase } from './queue';

// Server accepteert max 60 ids per call; ruim eronder blijven houdt ook de
// rij-limiet aan de serverkant (3000) buiten bereik.
const CHUNK_SIZE = 50;

export type MediaCounts = Record<string, number>;

/** Geregistreerde media per item (server). Ids zonder media ontbreken (= 0). */
export async function getServerMediaCounts(
  wiSfIds: string[],
  fase: MediaFase
): Promise<MediaCounts> {
  const { staff, pin } = useAuthStore.getState();
  if (!staff?.id || !pin || wiSfIds.length === 0) return {};

  const counts: MediaCounts = {};
  for (let i = 0; i < wiSfIds.length; i += CHUNK_SIZE) {
    const chunk = wiSfIds.slice(i, i + CHUNK_SIZE);
    try {
      const res = await fetch(
        `/api/media?wiSfIds=${encodeURIComponent(chunk.join(','))}&fase=${encodeURIComponent(fase)}`,
        { headers: { 'X-Staff-Id': staff.id, 'X-Staff-Pin': pin } }
      );
      if (!res.ok) continue;
      const json = (await res.json()) as { success: boolean; counts?: MediaCounts };
      if (json.success && json.counts) Object.assign(counts, json.counts);
    } catch {
      // Offline: dan telt alleen de lokale queue mee.
    }
  }
  return counts;
}

/**
 * Nog niet geüploade captures per item (lokale queue).
 *
 * NOOIT toArray() hier: elke rij draagt de foto-bytes, en "de queue is klein"
 * is precies de aanname die op 15-08 en 18-08 niet bleek te kloppen (212
 * items op één toestel). Eén rij tegelijk, zelfde patroon als de uploader.
 */
export async function getQueuedMediaCounts(fase: MediaFase): Promise<MediaCounts> {
  const sleutels = (await db.mediaQueue.orderBy('createdAt').primaryKeys()) as number[];
  const counts: MediaCounts = {};
  for (const sleutel of sleutels) {
    const row = await db.mediaQueue.get(sleutel);
    if (!row || row.fase !== fase || !row.wiSfId) continue;
    counts[row.wiSfId] = (counts[row.wiSfId] ?? 0) + 1;
  }
  return counts;
}
